import {useNavigate, useParams} from "react-router-dom";
import { calculateAge } from '../utils/age';
import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import {
  ArrowForward,
  ManageAccounts as ManageAccountsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  Wc as GenderIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
  HomeOutlined as HomeIcon,
  PersonOutline as PersonIcon,
  TrackChangesOutlined as TargetIcon,
  DescriptionOutlined as FormsIcon,
  StickyNote2Outlined as NotesIcon,
  FactCheckOutlined as CheckInIcon,
  Straighten as MeasurementsIcon,
  AutoAwesomeOutlined as SparkleIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useState} from "react";
import moment from "moment";

import ClientMainSectionV2 from "../componenets/client/v2/ClientMainSectionV2";
import ClientPageProfile from "../componenets/ClientPageProfile";
import ClientPageNotes from "../componenets/client/ClientPageNotes";
import ClientPageTargets from "../componenets/client/ClientPageTargets";
import ClientPageForms from "../componenets/client/ClientPageForms";
import ClientPageCheckIns from "../componenets/client/ClientPageCheckIns";
import ClientPageScheduledCheckIns from "../componenets/client/ClientPageScheduledCheckIns";
import ClientMeasurementSettings from "../componenets/client/measurements/ClientMeasurementSettings";
import ClientActionMenu from "../componenets/client/ClientActionMenu";
import TabsRow from "../componenets/dashboard/coach/TabsRow";
import {StatusChip} from "../componenets/ui";
import {getClient, getClientNotes} from "../services/TraineeService";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {experienceLevels} from "../enums/ExperienceLevels";
import {formatPhoneForDisplay} from "../componenets/shared/PhoneInput";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {ReactComponent as ActiveSvg} from "../assets/images/active.svg";
import {ReactComponent as InactiveSvg} from "../assets/images/inactive.svg";

const CLIENT_TAB_DEFS = [
  {key: "one", label: "ראשי", Icon: HomeIcon},
  {key: "two", label: "פרופיל", Icon: PersonIcon},
  {key: "three", label: "מטרות", Icon: TargetIcon},
  {key: "four", label: "טפסים", Icon: FormsIcon},
  {key: "five", label: "פתקים", Icon: NotesIcon},
  {key: "six", label: "צ'ק-אין", Icon: CheckInIcon},
  {key: "seven", label: "מדדים", Icon: MeasurementsIcon},
];

/* ── Detail cell (one field inside the profile card grid) ── */
const DetailCell = ({icon: Icon, label, value}) => (
    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
      <Box sx={{
        width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
        background: t.bg, color: t.t3,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon sx={{fontSize: 18}}/>
      </Box>
      <Box sx={{display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0}}>
        <Typography sx={{fontSize: "10.5px", fontWeight: 600, color: t.t3, letterSpacing: ".02em"}}>{label}</Typography>
        <Typography sx={{fontSize: "13px", fontWeight: 700, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{value}</Typography>
      </Box>
    </Box>
);

/* ═══════════════════════════════════════════════════════════
   Client Detail Page — coach-look redesign (v2)
   ═══════════════════════════════════════════════════════════ */
const ClientPageV2 = () => {
  const navigate = useNavigate();
  const params = useParams();
  const {notificationHandler} = useMessageContext();

  const [tabValue, setTabValue] = useState("one");
  const [client, setClient] = useState(null);
  const [reload, setReload] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleReload = useCallback(() => setReload(p => !p), []);

  // ── Data fetching ──────────────────────────────────────
  // A 403 here needs no local branch: the generated client's global interceptor reports it and
  // AccessDeniedProvider swaps this whole page for the refusal screen (see AccessDeniedContext).
  // The toast below is for genuine action failures only.
  useEffect(() => {
    getClient(params.userId)
        .then(r => setClient(r.data))
        .catch(e => {
          if (e.response?.status === 403) return;
          notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    getClientNotes(params.userId)
        .then(r => setHasNotes((r.data || []).length > 0))
        .catch(() => {});
  }, [reload, params.userId]);

  const clientTabs = CLIENT_TAB_DEFS.map(tab =>
      tab.key === "five" ? {...tab, hasAlert: hasNotes} : tab
  );

  // ── Helpers ────────────────────────────────────────────
  const age = client?.dateOfBirth ? calculateAge(client.dateOfBirth) : null;
  const genderText = client?.gender === "MALE" ? "זכר" : client?.gender === "FEMALE" ? "נקבה" : null;
  const expLevel = client?.experienceLevel ? experienceLevels.find(l => l.value === client.experienceLevel)?.name : null;
  const lastTrained = client?.lastTrained ? moment.unix(client.lastTrained).fromNow() : null;

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* ─── Header (on canvas) ──────────────────────── */}
        <Box sx={{display: "flex", flexDirection: "column", gap: 1.25, flexShrink: 0}}>
          {/* Top action bar */}
          <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
            <IconButton
                onClick={() => navigate("/app/clients")}
                sx={{color: t.t2, p: 1, "&:hover": {bgcolor: t.greenLight, color: t.green}}}
            >
              <ArrowForward/>
            </IconButton>
            <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.t2}}>חזרה ללקוחות</Typography>

            <Box sx={{flex: 1}}/>

            {/* Back to old design */}
            <Tooltip title="חזרה לעיצוב הישן">
              <Box component="button" onClick={() => navigate(`/app/clients/${params.userId}?classic=1`)} sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: t.white, color: t.t2,
                height: 34, px: "13px", borderRadius: t.rs,
                fontSize: "12.5px", fontWeight: 600,
                border: `1px solid ${t.border}`, cursor: "pointer", fontFamily: "inherit",
                transition: "all .14s",
                "&:hover": {borderColor: t.green, color: t.green, background: t.greenLight},
              }}>
                <SparkleIcon sx={{fontSize: 15}}/>
                עיצוב ישן
              </Box>
            </Tooltip>
          </Box>

          {/* Profile header card */}
          {client && (
              <Box sx={{
                background: t.white, border: `1px solid ${t.border}`,
                borderRadius: t.r, boxShadow: t.sh, overflow: "hidden",
              }}>
                {/* Identity row */}
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.25,
                  padding: {xs: "14px 16px", md: "16px 22px"},
                  borderBottom: `1px solid ${t.border}`,
                  background: `linear-gradient(135deg, ${t.greenLight}, #f4fbf8)`,
                }}>
                  <Box sx={{minWidth: 0, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}>
                    <Typography sx={{fontSize: {xs: "21px", md: "25px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                      {client.name || "פרטי לקוח"}
                    </Typography>
                    {client.enabled
                        ? <StatusChip label="פעיל" variant="success" icon={<ActiveSvg style={{width: 11, height: 11}}/>} size="small"/>
                        : <StatusChip label="חסום" variant="error" icon={<InactiveSvg style={{width: 11, height: 11}}/>} size="small"/>}
                  </Box>

                  <Box sx={{flex: 1}}/>

                  {/* 3-dot ActionMenu */}
                  <IconButton
                      onClick={e => setMenuAnchor(e.currentTarget)}
                      sx={{
                        color: t.t2, border: `1px solid ${t.border}`, background: t.white,
                        borderRadius: t.rs, width: 40, height: 40, flexShrink: 0,
                        "&:hover": {bgcolor: t.greenLight, borderColor: t.green, color: t.green},
                      }}
                  >
                    <ManageAccountsIcon/>
                  </IconButton>
                </Box>

                {/* Details grid */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)"},
                  gap: {xs: 1.75, md: 2.25},
                  padding: {xs: "16px", md: "18px 22px"},
                }}>
                  {client.email && <DetailCell icon={EmailIcon} label="מייל" value={client.email}/>}
                  {client.phoneNumber && <DetailCell icon={PhoneIcon} label="טלפון" value={formatPhoneForDisplay(client.phoneNumber)}/>}
                  {age && <DetailCell icon={CakeIcon} label="גיל" value={age}/>}
                  {genderText && <DetailCell icon={GenderIcon} label="מגדר" value={genderText}/>}
                  {expLevel && <DetailCell icon={SchoolIcon} label="רמה" value={expLevel}/>}
                  {lastTrained && <DetailCell icon={ScheduleIcon} label="אימון אחרון" value={lastTrained}/>}
                </Box>
              </Box>
          )}
        </Box>

        {/* ─── Pill tabs ───────────────────────────────── */}
        <TabsRow value={tabValue} onChange={setTabValue} tabs={clientTabs}/>

        {/* ─── Tab content ─────────────────────────────── */}
        <Box>
          {tabValue === "one" && <ClientMainSectionV2 userId={params.userId}/>}
          {tabValue === "two" && <ClientPageProfile handleReload={handleReload} client={client} coach/>}
          {tabValue === "three" && <ClientPageTargets client={client} coach/>}
          {tabValue === "four" && client && <ClientPageForms client={client} coach/>}
          {tabValue === "five" && client && (
              <ClientPageNotes client={client} coach onNotesChanged={setHasNotes}/>
          )}
          {tabValue === "six" && client && (
              <>
                <ClientPageCheckIns client={client} coach/>
                <ClientPageScheduledCheckIns client={client} coach/>
              </>
          )}
          {tabValue === "seven" && client && (
              <ClientMeasurementSettings traineeId={Number(params.userId)}/>
          )}
        </Box>

        {client && (
            <ClientActionMenu
                trainee={client}
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                onChanged={() => {
                  setMenuAnchor(null);
                  handleReload();
                }}
                onDeleted={() => {
                  setMenuAnchor(null);
                  navigate("/app/clients");
                }}
            />
        )}
      </Box>
  );
};

export default ClientPageV2;
